import React from 'react';
import { Check, Plus, X, AlertTriangle, Ban } from 'lucide-react';

/**
 * Individual Pill Component
 * 
 * Represents a single filter or output field option.
 * Status states:
 * - included: Active filter (green, ✓)
 * - suggested: Available to add (gray outline, +)
 * - excluded: Explicitly excluded / NOT logic (red, ✗)
 * 
 * Predictive states (for proactive guidance):
 * - unavailable: Would result in 0 matches (grayed out, disabled)
 * - warning: Would result in very few matches (orange indicator)
 */
function Pill({ 
  label, 
  status = 'suggested', 
  onClick, 
  disabled = false,
  size = 'default', // 'small' | 'default' | 'large'
  showIcon = true,
  // Predictive filtering props
  predictiveState = 'safe', // 'safe' | 'warning' | 'unavailable'
  estimatedCount = null, // Estimated results if this pill is selected
  showEstimate = false, // Show the estimated count badge
}) {
  // Determine if pill should be effectively disabled
  const isUnavailable = predictiveState === 'unavailable' && status === 'suggested';
  const isEffectivelyDisabled = disabled || isUnavailable;
  const showWarning = predictiveState === 'warning' && status === 'suggested';
  
  // Style variants based on status - DEX: More prominent check/X
  const statusStyles = {
    included: 'bg-emerald-100 border-2 border-emerald-500 text-emerald-800 shadow-sm hover:bg-emerald-200',
    suggested: showWarning 
      ? 'bg-amber-50 border border-amber-300 text-amber-700 border-dashed hover:bg-amber-100 hover:border-amber-400'
      : 'bg-white border border-gray-300 text-gray-600 border-dashed hover:bg-gray-50 hover:border-gray-400',
    excluded: 'bg-red-100 border-2 border-red-500 text-red-700 shadow-sm hover:bg-red-200',
  };
  
  // Unavailable style (overrides status style)
  const unavailableStyle = 'bg-gray-100 border border-gray-200 text-gray-400 border-dashed cursor-not-allowed';
  
  // Size variants
  const sizeStyles = {
    small: 'px-2.5 py-1 text-xs gap-1.5',
    default: 'px-3 py-1.5 text-xs gap-2',
    large: 'px-4 py-2 text-sm gap-2',
  };
  
  // Icon components based on status and predictive state
  const getStatusIcon = () => {
    if (isUnavailable) return Ban;
    if (showWarning) return AlertTriangle;
    return {
      included: Check,
      suggested: Plus,
      excluded: X,
    }[status];
  };
  
  const StatusIcon = getStatusIcon();
  
  // Icon sizes - DEX: Larger for prominence
  const iconSizes = {
    small: 'w-3.5 h-3.5',
    default: 'w-4 h-4',
    large: 'w-5 h-5',
  };
  
  // Generate tooltip based on state
  const getTooltip = () => {
    if (isUnavailable) {
      return `"${label}" would return 0 results with current filters`;
    }
    if (showWarning && estimatedCount !== null) {
      return `Adding "${label}" would show only ~${estimatedCount} results`;
    }
    if (status === 'included') {
      return `${label} is included (click to change)`;
    }
    if (status === 'excluded') {
      return `${label} is excluded (click to change)`;
    }
    if (estimatedCount !== null && showEstimate) {
      return `Click to include ${label} (~${estimatedCount} results)`;
    }
    return `Click to include ${label}`;
  };
  
  return (
    <button
      onClick={isEffectivelyDisabled ? undefined : onClick}
      disabled={isEffectivelyDisabled}
      className={`
        inline-flex items-center rounded-full font-medium
        border transition-all
        ${isUnavailable ? unavailableStyle : statusStyles[status]}
        ${sizeStyles[size]}
        ${isEffectivelyDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'}
        ${showWarning ? 'ring-1 ring-amber-300 ring-offset-1' : ''}
      `}
      title={getTooltip()}
    >
      {showIcon && StatusIcon && (
        <StatusIcon 
          className={`
            ${iconSizes[size]} flex-shrink-0
            ${isUnavailable ? 'text-gray-400' : ''}
            ${showWarning ? 'text-amber-600' : ''}
          `} 
        />
      )}
      <span className={`truncate max-w-[200px] ${isUnavailable ? 'line-through' : ''}`}>
        {label}
      </span>
      
      {/* Estimated count badge for warnings */}
      {showEstimate && estimatedCount !== null && status === 'suggested' && !isUnavailable && (
        <span className={`
          text-[10px] px-1 py-0.5 rounded-full ml-1
          ${showWarning ? 'bg-amber-200 text-amber-800' : 'bg-black/10 text-black/60'}
        `}>
          ~{estimatedCount}
        </span>
      )}
    </button>
  );
}

/**
 * Pill Group Component
 * 
 * Renders a labeled group of pills for a single filter category.
 */
export function PillGroup({ 
  label, 
  pills, 
  onPillClick, 
  type = 'multi-select',
  collapsible = false,
  defaultCollapsed = false,
}) {
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  
  // Count included pills for the label badge
  const includedCount = pills.filter(p => p.status === 'included').length;
  const excludedCount = pills.filter(p => p.status === 'excluded').length;
  
  // For single-select, only one can be included at a time
  const handlePillClick = (pillId, currentStatus) => {
    if (type === 'single-select' && currentStatus !== 'included') {
      // For single-select going to included, deselect others first
      onPillClick(pillId, currentStatus, true);
    } else {
      onPillClick(pillId, currentStatus, false);
    }
  };
  
  return (
    <div className="pill-group">
      <div 
        className={`flex items-center gap-2 mb-1.5 ${collapsible ? 'cursor-pointer' : ''}`}
        onClick={collapsible ? () => setCollapsed(!collapsed) : undefined}
      >
        <span className="text-xs font-medium text-black/60">{label}</span>
        
        {includedCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
            {includedCount} selected
          </span>
        )}
        
        {excludedCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded-full">
            {excludedCount} excluded
          </span>
        )}
        
        {type === 'single-select' && (
          <span className="text-[10px] text-black/40">(pick one)</span>
        )}
        
        {collapsible && (
          <span className="text-black/40 text-xs ml-auto">
            {collapsed ? '▸' : '▾'}
          </span>
        )}
      </div>
      
      {!collapsed && (
        <div className="flex flex-wrap gap-1.5">
          {pills.map(pill => (
            <Pill
              key={pill.id}
              label={pill.label}
              status={pill.status}
              onClick={() => handlePillClick(pill.id, pill.status)}
              size="default"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Output Field Pill
 * 
 * Simpler pill for output field selection (no excluded state).
 */
export function OutputFieldPill({ 
  label, 
  enabled = false, 
  onClick,
  isDefault = false,
}) {
  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium
        border transition-all cursor-pointer hover:scale-105
        ${enabled 
          ? 'bg-[#5a20ff]/10 border-[#5a20ff]/30 text-[#5a20ff]' 
          : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
        }
      `}
      title={enabled ? `${label} will be included in results` : `Click to include ${label}`}
    >
      {enabled ? (
        <Check className="w-2.5 h-2.5" />
      ) : (
        <Plus className="w-2.5 h-2.5 opacity-50" />
      )}
      <span>{label}</span>
      {isDefault && !enabled && (
        <span className="text-[9px] text-gray-400">(default)</span>
      )}
    </button>
  );
}

export default Pill;




